<template>
  <b-row>
    <b-col
      lg="10"
      xl="8"
    >
      <div
        class="card"
        style="height: 34rem"
      >
        <div
          class="card-header"
          v-if="accessPublisherSelection"
        >
          <div class="search-bar mr-2">
            <base-autocomplete
              id="search-publisher"
              class="w-100"
              data-test="name-filter"
              :items="itemsToSearch"
              :placeholder="'Search'"
              v-model="searchName"
              :search-text.sync="searchName"
              @update:searchText="onFilterChange"
              @keyup.native="onKeyUp"
            />
          </div>
          <div
            class="card-options"
            data-test="publisher-selection-view-toggle"
          >
            Verticals&nbsp;&nbsp;
            <b-form-checkbox
              v-model="showIndividualPublisherSelection"
              name="check-button"
              switch
              @change="updateSearchItems($event)"
            >
              Publishers
            </b-form-checkbox>
          </div>
        </div>
        <div
          class="card-body o-auto"
        >
          <div
            v-if="isLoading"
            class="loader m-auto"
          />
          <ul
            v-else-if="showIndividualPublisherSelection"
            class="list-unstyled list-separated"
            data-test="campaign-publisher-list"
          >
            <template
              v-for="publisher in displayedPublisherListOrdered"
            >
              <li
                :key="publisher.id"
                class="list-separated-item border-bottom"
                data-test="campaign-publisher-list-item"
              >
                <div class="row align-items-center">
                  <div
                    class="col-auto"
                    data-test="campaign-publisher-checkbox"
                  >
                    <b-form-checkbox
                      :id="`checkbox-${publisher.id}`"
                      v-model="publisher.status"
                      :name="`checkbox-${publisher.id}`"
                      @change="changePublisherStatus(publisher.id, $event)"
                      size="xl"
                      class="publisher-checkbox"
                      data-test="campaign-publisher-list-item-checkbox"
                      :disabled="GET_IS_CAMPAIGN_READONLY"
                    />
                  </div>
                  <div class="col-auto">
                    <base-company-logo
                      :company="publisher"
                    />
                  </div>
                  <div class="col">
                    <div
                      class="text-inherit"
                      data-test="campaign-publisher-list-item-name"
                    >
                      {{ publisher.name }}
                    </div>
                    <small class="d-block item-except text-sm h-1x">
                      <a
                        :href="`http://${publisher.domain}`"
                        target="_blank"
                      >
                        {{ publisher.domain }}
                      </a>
                    </small>
                  </div>
                  <div
                    class="col-3 text-center"
                    v-if="GET_IS_MANAGED_CAMPAIGN"
                  >
                    <div
                      v-if="publisherHasDeals(publisher.id)"
                    >
                      <div
                        :class="[getPublisherDealType(publisher.id) === 'Preferred' ? 'tag-azure' : 'tag-gray']"
                        class="text-inherit tag text-white"
                        data-test="campaign-publisher-list-item-name"
                      >
                        {{ getPublisherDealType(publisher.id) }}
                      </div>
                      <small
                        :data-test="`${publisher.name}-deal-collapse-button`"
                        class="d-block item-except text-sm text-muted h-1x text-underline"
                        v-b-toggle="`accordion-${publisher.id}-deal`"
                      >
                        <button
                          class="btn btn-sm btn-link small when-closed"
                        >
                          Show deals
                        </button>
                        <button
                          class="btn btn-sm btn-link small when-opened"
                        >
                          Hide deals
                        </button>
                      </small>
                    </div>
                    <small
                      :data-test="`${publisher.name}-creative-collapse-button`"
                      class="d-block item-except text-sm text-muted h-1x text-underline"
                      v-b-toggle="`accordion-${publisher.id}-creative`"
                    >
                      <button
                        class="btn btn-sm btn-link small when-closed"
                        :disabled="creatives.length < 1"
                      >
                        Show Creatives
                      </button>
                      <button
                        class="btn btn-sm btn-link small when-opened"
                      >
                        Hide Creatives
                      </button>
                    </small>
                  </div>
                </div>
              </li>
              <b-collapse
                :id="`accordion-${publisher.id}-deal`"
                :key="`publisher-deals-${publisher.id}`"
                accordion="my-accordion"
                class="py-4 pl-5 border-bottom"
                role="tabpanel"
                v-if="GET_IS_MANAGED_CAMPAIGN && publisherHasDeals(publisher.id)"
              >
                <table
                  class="table table border mb-0 publisher-deal-table"
                >
                  <tbody
                    class="border-left"
                  >
                    <tr
                      :key="`publisher-deal-${deal.id}`"
                      v-for="deal in publisherDeals[publisher.id].deals"
                    >
                      <td
                        class="text-right small"
                        width="1"
                      >
                        {{ getCreativeSize(deal.creativeSizeId) }}
                      </td>
                      <td class="small">
                        {{ deal.dealId }}
                      </td>
                      <td class="text-right small">
                        <span class="text-muted">
                          ${{ (deal.maxCpmMicros / 1000000).toFixed(2) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-collapse>
              <b-collapse
                v-if="GET_IS_MANAGED_CAMPAIGN"
                class="py-4 pl-5 border-bottom"
                :id="`accordion-${publisher.id}-creative`"
                :key="`publisher-creatives-${publisher.id}`"
                accordion="my-accordion"
                role="tabpanel"
              >
                <table
                  class="table table border mb-0 publisher-deal-table"
                >
                  <tbody
                    class="border-left"
                  >
                    <tr
                      v-for="creative in getActiveCreatives"
                      :key="`publisher-deal-${creative.id}`"
                    >
                      <td
                        class="text-left small"
                      >
                        {{ creative.name }}
                      </td>
                      <td class="text-right small custom-pub-c-slider">
                        <b-form-checkbox
                          name="check-button"
                          switch
                          :disabled="GET_IS_CAMPAIGN_READONLY || isOnlyEnabledCreativeForPublisher(publisher.id, creative.id)"
                          :checked="getPublishersCreativesSliderValue(publisher.id, creative.id)"
                          @change="updatePublishersCreativesState(publisher.id, creative.id, $event)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-collapse>
            </template>
          </ul>
          <template v-else>
            <group-publisher-selection
              :publisher-groups="publisherGroups"
              :displayed-publisher-groups="displayedPublisherGroups"
              :publishers="publishersList"
              :selected-publisher-ids.sync="publisherIds"
              :selected-publisher-groups.sync="selectedPublisherGroups"
              :disabled="disabled"
              @updateSelectedPublisherIds="updateSelectedPublisherIdsPayload"
              @updateSelectedGroups="updateSelectedGroups"
              @updateSelectedGroupIds="updateSelectedGroupIdsPayload"
            />
          </template>
        </div>
      </div>
      <div
        class="d-flex"
      >
        <small class="d-block text-muted">
          Logos provided by
          <a
            href="https://logo.dev"
            target="_blank"
          >Logo.dev</a>
        </small>
      </div>
    </b-col>

    <div class="col">
      <span
        class="badge badge-default"
        :class="{ 'bg-orange-light': reachedMinimumPublishers }"
      >{{ totalSelectedPublishers }} / {{ totalPublishers }} Publishers</span>
      <div class="mt-3">
        <manage-deals
          v-if="GET_IS_MANAGED_CAMPAIGN && publishersList.length > 0"
          class="ml-auto"
          :publishers="publishersList"
        />
      </div>
    </div>
  </b-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import feature from 'vue-feature-toggle';
import helper from '@/core/helper';

import GroupPublisherSelection from '@/modules/campaigns/components/GroupPublisherSelection';
import ManageDeals from '@/modules/campaigns/components/ManageDeals';
import BaseAutocomplete from '@/components/form/BaseAutocomplete.vue';
import BaseCompanyLogo from '@/components/BaseCompanyLogo.vue';

const MINIMUM_ENABLED_CREATIVES = 1;

export default {
  name: 'CustomPublishers',
  components: {
    BaseCompanyLogo,
    BaseAutocomplete,
    GroupPublisherSelection,
    ManageDeals,
  },
  props: {
    orderPayload: {
      type: Object,
      required: true,
    },
    orderSet: {
      type: [Array, Object],
      default: () => {},
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      isLoading: false,
      minimumPublishers: 1,
      accessPublisherSelection: true,
      searchName: '',
      showIndividualPublisherSelection: false,
      allFetchedPublishers: {},
      publisherDeals: {},
      creativeSizes: [],
      publishersCreatives: {},
      itemsToSearch: [],
      publishersList: [],
      displayedPublisherList: [],
      publisherGroups: [],
      selectedPublisherGroups: [],
      displayedPublisherGroups: [],
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
      'publisherIds',
      'campaign',
    ]),
    ...mapGetters('campaigns', [
      types.GET_IS_NEW_CAMPAIGN,
      types.GET_IS_CAMPAIGN_READONLY,
      types.GET_IS_MANAGED_CAMPAIGN,
    ]),
    ...mapState('content', [
      'creatives',
    ]),
    totalPublishers() {
      if (this.publishersList.length === 0) return '';
      return this.publishersList.length;
    },
    totalSelectedPublishers() {
      return this.publisherIds.length;
    },
    reachedMinimumPublishers() {
      return this.publisherIds.length === this.minimumPublishers;
    },
    publishersListAlphabetically() {
      return this.publishersList && this.publishersList.length > 0
        ? helper.orderBy(this.publishersList, 'name') : this.publishersList;
    },
    displayedPublisherListOrdered() {
      const selectedPublishers = this.displayedPublisherList.filter((publisher) => this.publisherIds.includes(publisher.id));
      const notSelectedPublishers = this.displayedPublisherList.filter((publisher) => !this.publisherIds.includes(publisher.id));
      return [...selectedPublishers.length > 0 ? helper.orderBy(selectedPublishers, 'name') : [],
        ...notSelectedPublishers.length > 0 ? helper.orderBy(notSelectedPublishers, 'name') : []];
    },
    orderPublishers() {
      return this.orderSet.publisherIds;
    },
    publisherGroupName() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'MANAGED' : 'SELF_SERVICE';
    },
    publisherGroupsSectorName() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'MANAGED_SECTOR' : 'SECTOR';
    },
    getActiveCreatives() {
      return this.creatives.filter((creative) => !creative.paused);
    },
  },
  methods: {
    ...mapActions('campaigns', [
      types.FETCH_PUBLISHERS,
      types.FETCH_PUBLISHERS_GROUPS,
      types.ADD_PUBLISHER,
      types.DELETE_PUBLISHER,
      types.UPDATE_PUBLISHER_GROUP,
      types.FETCH_PUBLISHER_DEALS,
      types.FETCH_CREATIVE_SIZES,
      types.GET_PUBLISHERS_CREATIVES,
    ]),
    getPublishersCreatives() {
      const { advertiserId, id } = this.campaign;
      if (!id) return;
      this.GET_PUBLISHERS_CREATIVES({ advertiserId, campaignId: id })
        .then((pCreatives) => {
          this.publishersCreatives = pCreatives;
        });
    },
    getPublishersCreativesSliderValue(publisherId, creativeId) {
      const publisherCreatives = this.publishersCreatives[publisherId];
      if (!publisherCreatives) {
        return this.publisherIds.includes(publisherId);
      }
      const creative = publisherCreatives.find((aCreative) => aCreative.creativeId === creativeId);
      return !creative?.isPaused;
    },
    isOnlyEnabledCreativeForPublisher(publisherId, creativeId) {
      const sliderValueForThisCreative = this.getPublishersCreativesSliderValue(publisherId, creativeId);
      const enabledCount = this.getActiveCreatives
        .filter((aCreative) => this.getPublishersCreativesSliderValue(publisherId, aCreative.id))
        .length;
      return sliderValueForThisCreative === true && enabledCount === MINIMUM_ENABLED_CREATIVES;
    },
    updatePublishersCreativesState(publisherId, creativeId, isPaused) {
      const defaultPublisherCreative = this.publishersCreatives[publisherId]?.filter((aCreative) => aCreative.creativeId === creativeId);
      if (this.publisherIds.includes(publisherId)) {
        this.$emit('updatePublishersCreativesState', {
          publisherId,
          creativeId,
          isPaused: !isPaused,
        }, defaultPublisherCreative);

        this.deactivatePublisherIfAllCreativesArePaused(creativeId, publisherId, isPaused);
      } else {
        this.updatePublisherAndCreativeStatesWhenPublisherIsNotIncluded(creativeId, publisherId);
      }
    },
    deactivatePublisherIfAllCreativesArePaused(creativeId, publisherId, isPaused) {
      const selectedCreative = this.publishersCreatives[publisherId].find((publisherCreative) => publisherCreative.creativeId === creativeId);
      selectedCreative.isPaused = !isPaused;

      const areAllCreativesPausedForPublisher = this.publishersCreatives[publisherId]
        .filter((publisherCreative) => this.getActiveCreatives.find((activeCreative) => activeCreative.id === publisherCreative.creativeId))
        .every((publisherCreative) => publisherCreative.isPaused === true);

      if (areAllCreativesPausedForPublisher) {
        this.changePublisherStatus(publisherId, false);
        this.getActiveCreatives
          .forEach((creative) => {
            this.$emit('updatePublishersCreativesState', {
              publisherId,
              creativeId: creative.id,
              isPaused: false,
            });
          });
      }
    },
    updatePublisherAndCreativeStatesWhenPublisherIsNotIncluded(creativeId, publisherId) {
      this.publisherIds.push(publisherId);
      this.updatePublishersList(this.publishersList);
      this.publishersCreatives[publisherId] = this.getActiveCreatives.map((creative) => ({
        creativeId: creative.id,
        isPaused: creativeId !== creative.id,
      }));
      this.getActiveCreatives.filter((creative) => creative.id !== creativeId)
        .forEach((creative) => {
          this.$emit('updatePublishersCreativesState', {
            publisherId,
            creativeId: creative.id,
            isPaused: true,
          });
        });
    },
    removePublisherFromPublishersCreativesList(publisherId) {
      this.publishersCreatives[publisherId] = this.getActiveCreatives.map((creative) => ({
        creativeId: creative.id,
        isPaused: true,
      }));
      this.$emit('removePublisherFromPublishersCreativesList', publisherId);
    },
    updatePublishersList(publishersList) {
      this.publishersList = publishersList
        .filter((publisher) => publisher?.id)
        .map((publisher) => {
          publisher.status = this.publisherIds.includes(publisher.id);
          return publisher;
        });
    },
    addAllPublisherIds() {
      this.orderPublishers.forEach((id) => {
        this.ADD_PUBLISHER(id);
      });
      this.updatePublishersList(this.publishersList);
    },
    togglePublisherStatus(id, toggleState) {
      if (toggleState) {
        this.ADD_PUBLISHER(id);
        this.publishersCreatives[id] = this.getActiveCreatives.map((creative) => ({
          creativeId: creative.id,
          isPaused: false,
        }));
      } else {
        this.DELETE_PUBLISHER(id);
        this.removePublisherFromPublishersCreativesList(id);
      }
      this.updatePublishersList(this.publishersList);
      this.updateSelectedPublisherIdsPayload();
    },
    updateSelectedPublisherIdsPayload() {
      if (this.GET_IS_NEW_CAMPAIGN) {
        this.orderPayload.publisherIds = this.publisherIds;
        this.$emit('updateIsOrderChanged');
        return;
      }
      if (!this.arePublisherIdsTheSame(this.publisherIds)) {
        this.orderPayload.publisherIds = this.publisherIds;
        if (!this.orderPayload.publisherGroupIds) {
          this.orderPayload.publisherGroupIds = this.orderSet.publisherGroupIds;
        }
      } else if (!this.orderPayload.publisherGroupIds || this.arePublisherGroupIdsTheSame(this.orderPayload.publisherGroupIds)) {
        this.cleanPublisherPayload();
      }
      this.$emit('updateIsOrderChanged');
    },
    updateSelectedGroupIdsPayload(ids) {
      if (this.GET_IS_NEW_CAMPAIGN) {
        this.orderPayload.publisherGroupIds = ids;
        this.orderPayload.publisherIds = this.publisherIds;
        this.$emit('updateIsOrderChanged');
        return;
      }
      if (!this.arePublisherGroupIdsTheSame(ids)) {
        this.orderPayload.publisherGroupIds = ids;
        if (!this.arePublisherIdsTheSame(this.publisherIds)) {
          this.orderPayload.publisherIds = this.publisherIds;
        }
      } else {
        delete this.orderPayload.publisherGroupIds;
        if (this.arePublisherIdsTheSame(this.publisherIds)) {
          delete this.orderPayload.publisherIds;
        }
      }
      this.$emit('updateIsOrderChanged');
    },
    cleanPublisherPayload() {
      delete this.orderPayload.publisherIds;
      delete this.orderPayload.publisherGroupIds;
    },
    arePublisherIdsTheSame(publisherIds) {
      return this.arePublisherListTheSame('publisherIds', publisherIds);
    },
    arePublisherGroupIdsTheSame(publisherGroupIds) {
      return this.arePublisherListTheSame('publisherGroupIds', publisherGroupIds);
    },
    arePublisherListTheSame(lisName, values) {
      if (!this.orderSet[lisName]) return false;
      const payloadList = new Set(values);
      const orderList = new Set(this.orderSet[lisName]);
      return payloadList.size === orderList.size && [...payloadList].every((v) => orderList.has(v));
    },
    changePublisherStatus(id, toggleState) {
      this.selectedPublisherGroups = [];
      delete this.orderPayload.publisherGroupIds;
      this.$emit('updateIsOrderChanged');
      this.UPDATE_PUBLISHER_GROUP([]);
      this.togglePublisherStatus(id, toggleState);
    },
    updateSelectedGroups(list) {
      this.selectedPublisherGroups = list;
      this.UPDATE_PUBLISHER_GROUP(list);
    },
    publisherHasDeals(id) {
      return this.publisherDeals[id] && this.publisherDeals[id].deals.length > 0;
    },
    getCreativeSize(id) {
      const creative = this.creativeSizes.find((item) => item.id === id);
      return `${creative.width}x${creative.height}`;
    },
    getPublisherDealType(id) {
      const publisher = this.publisherDeals[id];
      const dealType = publisher && publisher.deals.length ? publisher.deals[0].type : undefined;
      return new Map([
        ['AUCTION', 'Open Auction'],
        ['PREFERRED', 'Preferred'],
      ]).get(dealType) || '';
    },
    onKeyUp: helper.debounce(function getCampaignsFiltered() {
      this.updateDisplayedItems();
    }, 500),
    onFilterChange() {
      this.updateDisplayedItems();
    },
    changeToDefaultDisplayedLists() {
      this.displayedPublisherGroups = this.publisherGroups;
      this.displayedPublisherList = this.publishersListAlphabetically;
    },
    updateDisplayedItems() {
      if (!this.searchName) {
        this.changeToDefaultDisplayedLists();
        return;
      }
      const searchedItems = this.itemsToSearch
        .filter((item) => item.name.toUpperCase().includes(this.searchName.toUpperCase()));
      if (this.showIndividualPublisherSelection) this.displayedPublisherList = helper.orderBy(searchedItems, 'name');
      else this.displayedPublisherGroups = searchedItems;
    },
    updateSearchItems(isIndividualPublisherSelected) {
      this.searchName = '';
      this.updateDisplayedItems();
      this.itemsToSearch = isIndividualPublisherSelected ? this.publishersList : this.publisherGroups;
    },
  },
  async created() {
    this.isLoading = true;
    this.accessPublisherSelection = feature.isVisible('INDIVIDUAL_PUBLISHER_SELECTION') && !this.GET_IS_CAMPAIGN_READONLY;
    const fetchPublishers = await this.FETCH_PUBLISHERS();
    const fetchPublishersGroupsSelfServiceList = await this.FETCH_PUBLISHERS_GROUPS(this.publisherGroupName);
    const fetchPublishersGroups = await this.FETCH_PUBLISHERS_GROUPS(this.publisherGroupsSectorName);
    Promise.all([fetchPublishers, fetchPublishersGroups, fetchPublishersGroupsSelfServiceList])
      .then(() => {
        const selfServiceList = fetchPublishersGroupsSelfServiceList[0].publisherIds
          .map((publisherId) => fetchPublishers
            .find((publisher) => publisher.id === publisherId));
        if ((Object.keys(this.orderSet).length !== 0)) {
          if (this.orderSet.publisherGroupIds.length !== 0) {
            this.selectedPublisherGroups = fetchPublishersGroups
              .filter((group) => this.orderSet.publisherGroupIds.includes(group.id))
              .map((item) => item.name);
            this.showIndividualPublisherSelection = false;
            this.itemsToSearch = fetchPublishersGroups;
            this.addAllPublisherIds();
            this.UPDATE_PUBLISHER_GROUP(this.selectedPublisherGroups);
          } else if (this.orderSet.publisherIds.length !== 0) {
            this.updatePublishersList(selfServiceList);
            this.showIndividualPublisherSelection = true;
            this.itemsToSearch = fetchPublishers;
            this.addAllPublisherIds();
          }
        } else {
          this.publisherGroups = fetchPublishersGroups;
          this.allFetchedPublishers = selfServiceList;
          this.updatePublishersList(selfServiceList);
        }
        this.publisherGroups = fetchPublishersGroups;
        this.allFetchedPublishers = selfServiceList;
        this.displayedPublisherList = this.publishersListAlphabetically;
        this.displayedPublisherGroups = this.publisherGroups;
        this.updatePublishersList(selfServiceList);
        this.updateSearchItems(this.showIndividualPublisherSelection);
        this.isLoading = false;
      });
    if (this.GET_IS_MANAGED_CAMPAIGN) {
      await this.FETCH_CREATIVE_SIZES().then((sizes) => { this.creativeSizes = sizes; });
      this.FETCH_PUBLISHER_DEALS().then((deals) => {
        this.publisherDeals = deals.reduce((accu, { publisherId, ...rest }) => {
          if (!accu[publisherId]) accu[publisherId] = { deals: [rest] };
          else accu[publisherId] = { deals: [...accu[publisherId].deals, rest] };
          return accu;
        }, {});
      });
      this.getPublishersCreatives();
    }
  },
  watch: {
    orderPublishers(newVal, old) {
      if (newVal !== old) this.updatePublishersList(this.publishersList);
    },
    showIndividualPublisherSelection(newVal) {
      if (newVal) this.updatePublishersList(this.publishersList);
    },
  },
};
</script>
<style scoped lang="scss">
.custom-pub-c-slider{
  .custom-switch{
    display: unset;
  }
}
.search-bar {
  width: 57%;
}
</style>
